import React, { useEffect, useMemo, useState } from "react";
import { useZipCodeValue, useRsnFeeValue, useApiPaths, useZipCode, useRsnFee, useCountyOptions } from "@directv-digital/dds.server.application-context";
import { fireTabHandleChange } from "@directv-digital/dds.services.analytics-service";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";

const bps = DtvTheme.breakpoints;
import { ZipcodeModuleProps, defaultCiConfig } from "./zipcode-module.props";
import { useLocationHref } from "src/hooks/useLocationHref";

// APIs
import LocalRsnChnlService, { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setCurrentZipcodeCookie,
  getZipcodeWithSignature,
  ZipcodeValidator,
  getDmaCookies,
  setDmaCookies,
  getCookie,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setCookie,
  isValidRsnFee,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formatRsnFeeAsTwoDecString
} from "@directv-digital/dds.services.cookie-service";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { fetchStreamPackageRsnFee } from "@directv-digital/dds.apis.epoch-apis";
import {
  getCountiesByZipcode,
  County
} from "@directv-digital/dds.apis.county-lookup";

import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import Button from "@directv-digital/fusion.atoms.button";
import { LoadingDots } from "@directv-digital/dds.ui.loading-text";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Link from "@directv-digital/fusion.atoms.link";
import TextField from "src/components/video-lab/text-field";
import TextFieldDropdown from "src/components/video-lab/text-field-dropdown";
import DtvAlert from "@directv-digital/fusion.molecules.alert";
import type { Breakpoint } from "@mui/material/styles";

const COUNTY_API_DELAYED_MS = 5000; // 5 seconds
const COUNTY_API_TIMEOUT_MS = 30000; // 30 seconds
const SUCCESS_MSG_TIMEOUT_MS = 5000; // 5 seconds

export const ZipcodeModule = React.memo((
  props: ZipcodeModuleProps
) => {
  const {
    id = "Zipcode-Module",
    mode = "dark",
    size = "medium",
    position = "center",
    onSearchButtonClick = () => ({}), // empty function if not provided
    onRsnFeeConfirmed = () => ({}),
    onRsnFeeApiError: _onRsnFeeApiError = () => ({}),
    onLocalRsnChnlsConfirmed = null,
    onLocalRsnChnlsApiError = () => ({}),
    onLocalRsnChnlApiLoading = () => ({}),
    onCacheConfirmed = () => ({}),
    onCountyApiError = () => ({}),
    localRsnApiOptions = {
      satellite: false,
      stream: true
    },
    initData = null,
    isConditional,
    ciConfig = defaultCiConfig,
    componentType: _componentType
  } = props;
  try {
    // Generated by Copilot- Start - 2026-04-01 20:31:38
    const appContextZip = useZipCodeValue() || "";
    const appContextRsnFee = useRsnFeeValue() || "";
    const apiPaths = useApiPaths();
    const { update: updateZipCode = () => undefined, value: zipCodeValue } = useZipCode() ?? {};
    const { update: updateRsnFeeCtx = () => undefined } = useRsnFee() ?? {};
    const { value: countyOptionsValue, update: updateCountyOptions = () => undefined } = useCountyOptions() ?? {};
    // Generated by Copilot- End - 2026-04-01 20:31:38
    /** Component */
    const inputRef = React.useRef() || {
      current: { focus: () => Promise.resolve() }
    };
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [showSuccessMsg, setShowSuccessMsg] = useState<boolean>(false);
    const successMsgTimeoutRef = React.useRef<ReturnType<
      typeof setTimeout
    > | null>(null);

    /** Text display */
    // Generated by Copilot- Start - 2026-04-15 14:21:57
    const [displayZip, setDisplayZip] = useState<string>(
      (isConditional && initData?.zipcode) ? initData.zipcode : (appContextZip || "")
    );
    // Generated by Copilot- End - 2026-04-15 14:21:57
    const [displayCounty, setDisplayCounty] = useState<County | null>(null);

    /** Zipcode/County Fields */
    // eslint-disable-next-line no-undef
    const zipcodeInputRef = React.useRef<FusionTextFieldRef>(null);
    // Generated by Copilot- Start - 2026-04-15 14:21:57
    const [inputZip, setInputZip] = useState<string>(
      (isConditional && initData?.zipcode) ? initData.zipcode : (appContextZip || "")
    );
    // Generated by Copilot- End - 2026-04-15 14:21:57
    const [zipInputError, setZipInputError] = useState<boolean>(false);
    const [countyOptions, setCountyOptions] = useState<County[] | null>(null);
    const [lastCountyOptions, setLastCountyOptions] = useState<County[] | null>(
      null
    );
    const [countySelection, setCountySelection] = useState<County | null>(null);
    const [countySelectionError, setCountySelectionError] =
      useState<boolean>(false);
    const [_changeFocused, setChangeFocused] = useState<boolean>(false);
    const [viewContext, setViewContext] = useState<'page' | 'modal'>('page');

    /** API State */
    const [isCountyApiLoading, setIsCountyApiLoading] = useState<boolean>(false);
    const [countyNoResults, setCountyNoResults] = useState<boolean>(false);
    const [countyApiHanging, setCountyApiHanging] = useState<boolean>(false);
    const [countyApiError, setCountyApiError] = useState<boolean>(false);

    /** Prefetching Local/Rsn API **/
    /** when search button clicked and data is still being fetched, set flag
     *  true, once data comes, send callback turn back to false*/
    const [localRsnCallbackNeeded, setLocalRsnCallbackNeeded] =
      useState<boolean>(false);
    /** Save the latest value, to send when requested by HOC */
    const [cachedZipcode, setCachedZipcode] = useState<string>("");
    const [cachedDmas, setCachedDmas] = useState<{
      dma: string;
      nielsenDma: string;
    } | null>(null);
    const [cachedLocalRsnData, setCachedLocalRsnData] =
      useState<LocalRsnApiResponse | null>(null);

    /** Theming/Styling */
    const mainTextColor = mode === "dark" ? "#FFF" : "#102641";
    const changeTextColor = mode === "dark" ? "#1FBCF8" : "#2E70BD";
    const hAlignment =
      position === "center" ?
        "center" :
        position === "right" ?
          "flex-end" :
          "flex-start";
    const displayTextVariant =
      size === "small" ? "body2medium" : "subhead2medium";

    // Custom switch to use container queries for in-page module
    const up = React.useCallback(
      (key: number | Breakpoint) => (position === "left" ?
          `@container (min-width: ${
              typeof key === "number" ? key : bps[key]
            }px)` :
        bps.up(key)),
      [bps.up, position]
    );
    const down = React.useCallback(
      (key: number | Breakpoint) => (position === "left" ?
          `@container (max-width: ${
              typeof key === "number" ? key - 1 : bps[key] - 1
            }px)` :
        bps.down(key)),
      [bps.down, position]
    );
    const locationHrefurl = useLocationHref();
    // Replace missing values with defaults
    const {
      displayText,
      displayTooltip,
      zipCodeInputErrorMsg,
      zipCodeLookupErrorMsg,
      countySelectionErrorMsg,
      countyApiHangingMsg,
      countyApiErrorMsg,
      drawerOpenText,
      drawerCloseText,
      zipCodeLabel,
      countyLabel,
      ctaLabel
    } = useMemo(() => {
      // If ciConfig is null or undefined, return all defaults
      if (!ciConfig) {
        return defaultCiConfig;
      }

      return Object.keys(defaultCiConfig).reduce(
        (config, key) => ({
          ...config,
          [key]:
            ciConfig[key] === undefined || ciConfig[key] === null ?
              defaultCiConfig[key] :
              ciConfig[key]
        }),
        {} as ZipcodeModuleProps["ciConfig"]
      );
    }, [ciConfig]);

    /* --- Cache API Calls---------------------------------------------------------------------- */
    const getLocalRsnChannels = async ({
      zipcode,
      dma,
      nielsenDma
    }): Promise<{ localChannels?: any[]; rsnChannels?: any[] }> => {
      const params = {
        context: { apiPaths },
        zipcode,
        dma,
        nielsenDma
      };
      onLocalRsnChnlApiLoading(true);
      // eslint-disable-next-line no-return-await
      return await LocalRsnChnlService.getChannels(params, localRsnApiOptions)
        .then(
          (data: LocalRsnApiResponse) => {
            onLocalRsnChnlApiLoading(false);
            return Promise.resolve(data);
          },
          error => {
            onLocalRsnChnlApiLoading(false);
            throw new Error(error);
          }          
        )
        ?.catch(err => {
          console.error("[ZipcodeModule] !! Local/Rsn API Error !!", err);
          onLocalRsnChnlsApiError(err);
          onLocalRsnChnlApiLoading(false);
          return Promise.reject(null);
        });
    };

    const getCounties = async (
      searchZipcode: string
    ): Promise<{ fetchedCounties?: County[] | null; fetchedZip?: string }> => {
      let delayTimeoutId: ReturnType<typeof setTimeout>;
      let failureTimeoutId: ReturnType<typeof setTimeout>;

      try {
        delayTimeoutId = setTimeout(() => {
          setCountyApiHanging(true);
        }, COUNTY_API_DELAYED_MS);

        const apiTimeout = new Promise<null>(() => {
          failureTimeoutId = setTimeout(() => Promise.reject("County API request timed out"), COUNTY_API_TIMEOUT_MS);
        });

        const fetchPromise = await getCountiesByZipcode(
          { apiPaths },
          searchZipcode
        ).then((resp: any) => {
          if (resp?.error || resp === undefined) {
            return Promise.reject("County data not found");
          }
          return Promise.resolve({
            fetchedCounties: resp,
            fetchedZip: searchZipcode
          });
        });

        return Promise.race([fetchPromise, apiTimeout]);
      } catch (err: any) {
        if ((err as Error)?.name === "AbortError") {
          console.error(
            "[ZipcodeModule] beamlocalchannellookupms -> getCountiesByZipcode timed out !! ",
            err
          );
        } else {
          console.error(
            "[ZipcodeModule] beamlocalchannellookupms -> getCountiesByZipcode API Error !! ",
            err
          );
        }
        onCountyApiError(err);
        return Promise.reject(err);
      } finally {
        clearTimeout(delayTimeoutId);
        clearTimeout(failureTimeoutId);
      }
    };
    /** ---------------------------------------------------------------------------------------- */
    /* --- Handle API Calls and data caching --- */
    const syncIncomingData = async ({
      contextZip,
      contextFee: _contextFee,
      dma,
      nielsenDma
    }: {
      contextZip: string;
      contextFee: string;
      dma: string;
      nielsenDma: string;
    }) => {
      const zipCode = getZipcodeWithSignature(contextZip);
      const isNewZip = displayZip !== zipCode.value;

      if (!isNewZip && displayCounty && isCountyApiLoading) {
        console.debug(
          "[ZipcodeModule] cancelled data sync - zip code and counties are equal to current.",
          id,
          displayZip,
          zipCode.value,
          contextZip
        );
        return;
      }

      // Always update display with the best found zip
      setDisplayZip(zipCode.value);

      console.debug(
        `[ZipcodeModule] syncing incoming data...\n`,
        `In Component id: ${id}\n`,
        `context zip: '${appContextZip}' currentZipcode: '${getCookie(
          "currentZipcode"
        )}'\n edgescape zip: '${
          decodeURIComponent(getCookie("x-dtv-edgescape") || "")
            ?.match(/\d{5}/)
            ?.join() || ""
        }' display zip: '${displayZip}'\n`,
        `best zip: ${zipCode.value} from ${zipCode.origin}\n`,
        `context RSN fee: '${appContextRsnFee}',\n`,
        `cookie  RSN fee: '${getCookie("rsnFee")}',\n`,
        `dma: '${dma}',\n`,
        `nielsenDma: '${nielsenDma}'`
      );

      if (zipCode.origin === "context" || zipCode.origin === "currentZipcode") {
        setInputZip(zipCode.value);

        // must be from other zipcode update, so clear cache
        if (zipCode.origin === "context") {
          zipcodeInputRef?.current?.clear();
        }
      }

      if (!(dma || nielsenDma)) {
        // on load with edgescape data
        setIsVisible(true);
        if (inputRef?.current?.focus) {
          inputRef.current.focus();
        }
      }

      handleCountySearch({
        searchZip: zipCode.value,
        trigger: "syncload"
      });
    };

    const handleCountySearch = ({
      searchZip,
      trigger
    }: {
      searchZip: string;
      trigger: string;
    }) => {
      if (searchZip === cachedZipcode) {
        console.log("Found same zip, setting cached values");
        setCountyOptions(lastCountyOptions);
        return;
      }
      if (isCountyApiLoading) {
        console.debug("[ZipcodeModule] County API is already loading");
        return;
      }

      // -- Look County info in Context - Back Sync - skip getCounties
      const countiesInContext = countyOptionsValue;
      try {
        if (
          trigger === "syncload" &&
          appContextZip &&
          countiesInContext?.length && countiesInContext?.[0] && countiesInContext?.[0]?.zipcode === appContextZip
        ) {
          if (
            (!countyOptions?.length) ||
            (countyOptions?.length && countyOptions?.[0] && countiesInContext?.[0]?.zipcode !== countyOptions?.[0]?.zipcode)
          ) {
            setDisplayCounty(countiesInContext[0]);
            setCountySelection(countiesInContext[0]);
            setCountyOptions(countiesInContext);
            setLastCountyOptions(countiesInContext);
            setCachedDmas({
              dma: countiesInContext[0]?.dtvDMA,
              nielsenDma: countiesInContext[0]?.nielsenDMA
            });
          }
          cacheLocaleData(appContextZip, countiesInContext[0], true)
            .then(({ resLocalRsns }) => {
              onCacheConfirmed(
                appContextZip,
                countiesInContext,
                appContextRsnFee,
                resLocalRsns
              );
            })
            .catch(err => {
              console.error(
                "[ZipcodeModule] cacheLocaleData error in context back sync:",
                err
              );
            });
          return;
        }
      } catch (err) {
        console.error(
          "[ZipcodeModule] Error during context county back sync:",
          err
        );
      }

      // -- Set existing values to null + loading state
      setCountyOptions([]);
      setCountyApiError(false);
      setCountyApiHanging(false);
      setCachedZipcode("");
      setCachedDmas(null);
      setIsCountyApiLoading(true);
      getCounties(searchZip)
        .then(({ fetchedCounties, fetchedZip }) => {
          console.debug("[ZipcodeModule] got counties >>", fetchedCounties);
          if (!fetchedCounties?.length) {
            setCountyNoResults(true);
            setIsCountyApiLoading(false);
            return;
          }
          // -- Earliest a zipcode can be confirmed as valid, so set cookie
          setCachedZipcode(fetchedZip);
          setCountyNoResults(false);
          setIsCountyApiLoading(false);
          setCountyApiHanging(false);

          // -- Update Context Counties when trigger is "syncload" and Context Counties are empty
          if (trigger === "syncload" && (!countiesInContext?.length || !appContextZip)) {
            updateCountyOptions(fetchedCounties);
            // if context zipcode is empty, along with counties update context zipcode
            if (!zipCodeValue) {
              const rsnFeeCookie = getCookie("rsnFee");
              if (rsnFeeCookie) {
                updateZipCode(fetchedZip, false);
                updateRsnFeeCtx(rsnFeeCookie);
              }
            }
          }

          // -- Always set options
          setCountyOptions(fetchedCounties);
          setLastCountyOptions(fetchedCounties);
          // Reset county selection if multiple counties and not triggered by syncload
          if (fetchedCounties?.length > 1 && trigger !== "syncload") {
            setCountySelection(null);
            // Do NOT clear displayCounty here; keep previous county visible while loading
          }
          let countyToSelect: County | null = null;
          let countyToPrefetch: County = fetchedCounties?.[0];

          // -- Always display the county if one fetched
          if (fetchedCounties?.length === 1) {
            countyToSelect = fetchedCounties?.[0];
            setCountySelectionError(false);
          }

          if (trigger === "syncload") {
            // -- If triggered from page load or context sync (NOT classic search) and
            //    only one county matches existing DMA (usually multiple, but not always), select it.
            // -- Otherwise, select the first county.
            // -- later enhancement: select exact county based on cookie
            const matchingDma: County[] = fetchedCounties?.filter(
              c => c?.dtvDMA === getDmaCookies()?.currentDma
            );
            if (matchingDma?.length === 1) {
              countyToSelect = matchingDma?.[0];
              countyToPrefetch = countyToSelect;
              setDisplayCounty(countyToSelect);
            } else {
              setDisplayCounty(fetchedCounties?.[0]);
            }
          }

          if (countyToSelect !== null) {
            setCountySelection(countyToSelect);
            setCachedDmas({
              dma: countyToSelect?.dtvDMA,
              nielsenDma: countyToSelect?.nielsenDMA
            });
          }

          // -- Fetch Local RSN channels to callback or cache for later
          return cacheLocaleData(
            fetchedZip,
            countyToPrefetch,
            Boolean(trigger === "syncload")
          ).then(({ resLocalRsns }) => {
            if (trigger === "syncload") {
              onCacheConfirmed(
                fetchedZip,
                fetchedCounties,
                appContextRsnFee,
                resLocalRsns
              );
            }
          });
        })
        .catch(() => {
          // If county API fails, should we call Epoch anyways?
          setCountyApiError(true);
          setCountyApiHanging(false);
          setCountyNoResults(false);
          setIsCountyApiLoading(false);

          // If county API fails, try to get EPOCH anyways
          try {
            if (isValidRsnFee(appContextRsnFee)) {
              onRsnFeeConfirmed(appContextRsnFee);
            }
          } catch (error) {
            console.error("[ZipcodeModule] Failed to confirm RSN fee from context.", error);
          }
        });
    };

    /** Concurrently fetch RSN Fee and Local RSN channels
     *  - either cache the data or send it to parent
     *  - skips calling local rsn chnls if the dma is the same as cached
     */
    const cacheLocaleData = async (
      searchZip: string,
      firstCounty: County,
      onLoad: boolean
    ) => {
      const localRsnPromise =
        firstCounty?.dtvDMA === cachedDmas?.dma ?
          Promise.resolve(cachedLocalRsnData) :
          getLocalRsnChannels({
            zipcode: searchZip,
            dma: firstCounty?.dtvDMA,
            nielsenDma: firstCounty?.nielsenDMA
          });

      setCachedLocalRsnData(null);
      // eslint-disable-next-line no-return-await
      return await Promise.allSettled([localRsnPromise]).then(results => {
        const resLocalRsns =
          results?.[0]?.status === "fulfilled" ? results?.[0]?.value : null;

        console.debug(
          `[ZipcodeModule] got local rsn channels, ${
            onLoad || localRsnCallbackNeeded ?
              "caching and sending callback" :
              "caching data only"
          } >>`,
          resLocalRsns
        );

        if (onLoad || localRsnCallbackNeeded) {
          onLocalRsnChnlsConfirmed(
            resLocalRsns,
            firstCounty?.dtvDMA,
            firstCounty?.nielsenDMA
          );
          setLocalRsnCallbackNeeded(false);
          setCachedLocalRsnData(() => resLocalRsns);
        } else if (
          results?.[0]?.status === "fulfilled" &&
          Boolean(results?.[0]?.value)
        ) {
          setCachedLocalRsnData(() => resLocalRsns);
        }

        return { resLocalRsns };
      });
    };
    /** ---------------------------------------------------------------------------------------- */

    /* --- Input Handlers --- */
    /** On County Dropdown change:
     * - update county selection
     * - If dma changes, updated cached data
     */
    const handleCountySelectionChange = val => {
      const countyObj = countyOptions?.find(
        item => item?.getKey() === val?.value
      );
      setCountySelection(countyObj);
      setCountySelectionError(false);

      // Use Case: user enters a zip, removes part of it, then changes county
      // If the county is not found, reset the selection
      if (
        inputZip?.length < 5 &&
        cachedZipcode?.includes(inputZip) &&
        cachedZipcode !== ""
      ) {
        setInputZip(cachedZipcode);
      }

      // If the dma of the new county is different (not usually the case), re-cache dmas
      if (
        cachedDmas?.dma !== countyObj?.dtvDMA ||
        cachedDmas?.nielsenDma !== countyObj?.nielsenDMA
      ) {
        setCachedDmas({
          dma: countyObj?.dtvDMA,
          nielsenDma: countyObj?.nielsenDMA
        });
        // If dmas change and a channels callback exists, also re-cache channels
        if (onLocalRsnChnlsConfirmed !== null) {
          getLocalRsnChannels({
            zipcode: cachedZipcode,
            dma: countyObj?.dtvDMA,
            nielsenDma: countyObj?.nielsenDMA
          }).then(data => setCachedLocalRsnData(() => data));
        }
      }
    };

    const handleZipInputChange = (e: any) => {
      const zipText = e?.target?.value; // ZipcodeValidator.stripNonNumbers()?.slice(0, 5);
      setInputZip(zipText);
      setZipInputError(false);

      if (ZipcodeValidator.isValidFull(zipText) && inputZip !== displayZip) {
        handleCountySearch({
          searchZip: zipText,
          trigger: "userinput"
        });
      }
    };

    const handleSearchButtonClick = async () => {
      if (!ZipcodeValidator.isValidFull(inputZip)) {
        setZipInputError(true);
        return;
      }
      if (countySelection === null) {
        setCountySelectionError(true);
        return;
      }

      console.debug(
        `[ZipcodeModule] handlingSearchButtonClick\n`,
        `cachedZip: ${cachedZipcode}\n`,
        `county: `,
        countySelection,
        `previous RSN fee: ${appContextRsnFee}\n`,
        `needsToSendLocalRsnCallback: ${localRsnCallbackNeeded}\n`,
        `cachedLocalRsnData:`,
        cachedLocalRsnData
      );

      if (cachedZipcode !== "") {
        let success = true;
        setInputZip(cachedZipcode);
        setDisplayZip(cachedZipcode);
        setDisplayCounty(countySelection);

        onSearchButtonClick(
          cachedZipcode,
          countyOptions,
          appContextRsnFee,
          cachedLocalRsnData,
          countySelection
        );
        if (updateZipCode !== undefined) {
          updateZipCode(cachedZipcode, true);
          updateCountyOptions(countyOptions);
        }

        if (isValidRsnFee(appContextRsnFee)) {
          console.debug(
            "[ZipcodeModule] sent rsn fee callback (search click)",
            appContextRsnFee
          );
          try {
            onRsnFeeConfirmed(appContextRsnFee);
          } catch {
            success = false;
          }
        }

        if (cachedLocalRsnData !== null) {
          console.debug(
            "[ZipcodeModule] sent local/rsn callback (search click)"
          );
          try {
            onLocalRsnChnlsConfirmed(
              cachedLocalRsnData,
              cachedDmas?.dma,
              cachedDmas?.nielsenDma
            );
          } catch {
            success = false;
          }
          setLocalRsnCallbackNeeded(false);
        } else {
          success = false;
          setLocalRsnCallbackNeeded(true);
        }

        if (cachedDmas?.dma !== "" && cachedDmas?.nielsenDma !== "") {
          setDmaCookies(cachedDmas?.dma, cachedDmas?.nielsenDma);
        }

        console.log(
          `[ZipcodeModule] Send rsn fee and local/rsn's on search buttonClick >> ${
            success ? "SUCCESS" : "NOT READY"
          }`
        );
        if (success) {
          triggerSuccessNotification();
        }
      }

      viewContext === 'modal' && (
        fireTabHandleChange({
          event: "trackEvent",
          event_name: "link_click",
          linkName: ctaLabel?.toLowerCase(),
          linkPosition: '/channel-lineup/modal/',
          linkDestination: 'modal_channel lineup'
        })
      );
    };

    const toggleVisibility = () => {
      const cmpElem = document.querySelector(`#${id}`) as HTMLElement;
      const distToTop = cmpElem?.getBoundingClientRect()?.top || 0;
      if (
        !isVisible &&
        (distToTop < 100 || distToTop > window.innerHeight / 2)
      ) {
        window.scrollBy({
          top: distToTop - window.innerHeight / 4,
          behavior: "smooth"
        });
      }
      setIsVisible(prev => !prev);
    };

    const onZipcodeCleared = () => {
      setInputZip("");
      setZipInputError(false);
      setCountySelection(null);
    };

    const triggerSuccessNotification = () => {
      setShowSuccessMsg(true);

      // clear existing timeout
      if (successMsgTimeoutRef?.current) {
        clearTimeout(successMsgTimeoutRef.current);
      }
      successMsgTimeoutRef.current = setTimeout(() => {
        setShowSuccessMsg(false);
        setIsVisible(false);
        successMsgTimeoutRef.current = null;
      }, SUCCESS_MSG_TIMEOUT_MS);
    };

    /** ---------------------------------------------------------------------------------------- */
    /** Render */
    /* ----------------------------------------------------------------------------------------
     *  --- Render Starts */
    // Load incoming initializer data
    //  - Should mimic the same flow as syncIncomingData -> handleCountySearch -> cacheLocaleData
    useEffect(() => {
      if (isConditional && initData?.zipcode) {
        setDisplayZip(initData?.zipcode || "");

        if (
          initData?.zipcode === (getCookie("currentZipcode") || "") ||
          initData?.zipcode === appContextZip
        ) {
          setInputZip(initData?.zipcode || "");
        }

        if (initData?.counties && initData?.counties?.length > 0) {
          setCountyOptions(initData?.counties);
          setLastCountyOptions(initData?.counties);
          if (initData?.counties?.length > 1 && initData?.county) {
            const cnty = initData?.counties?.find(
              c => c.getKey() === initData?.county.getKey()
            );
            setCountySelection(cnty);
            setDisplayCounty(cnty);
          } else {
            setCountySelection(initData?.counties?.[0]);
            setDisplayCounty(initData?.counties?.[0]);
          }
        // Generated by Copilot- Start - 2026-04-13 14:06:05
        } else if (initData?.zipcode) {
          // No counties pre-provided but zipcode is available — trigger county lookup.
          handleCountySearch({
            searchZip: initData.zipcode,
            trigger: 'syncload'
          });
        } else {
          setCountyApiError(true);
        }
        // Generated by Copilot- End - 2026-04-13 14:06:05
      }
    // Generated by Copilot- Start - 2026-04-15 15:50:32
    }, [isConditional, initData?.zipcode, initData?.counties, initData?.county]);
    // Generated by Copilot- End - 2026-04-15 15:50:32

    /** Call sync to load incoming data (both on load and on context update) */
    useEffect(() => {
      // Generated by Copilot- Start - 2026-04-15 14:21:57
      if (isConditional && initData?.zipcode) {
        return;
      }
      // Generated by Copilot- End - 2026-04-15 14:21:57

      const { currentDma, nielsenDma } = getDmaCookies();
      // Generated by Copilot- Start - 2026-04-15 13:56:21
      const resolvedZip = appContextZip || "";
      // Generated by Copilot- End - 2026-04-15 13:56:21
      syncIncomingData({
        contextZip: resolvedZip,
        contextFee: appContextRsnFee || "",
        dma: currentDma || "",
        nielsenDma: nielsenDma || ""
      });
    // Generated by Copilot- Start - 2026-04-15 15:50:32
    }, [appContextZip, isConditional, initData?.zipcode]);
    // Generated by Copilot- End - 2026-04-15 15:50:32

    // Once RSN Fee fetched in Context Send it to parent
    useEffect(() => {
      if (isValidRsnFee(appContextRsnFee)) {
        onRsnFeeConfirmed(appContextRsnFee);
        console.debug("[ZipcodeModule] received RSN fee:", appContextRsnFee);
      }
    }, [appContextRsnFee]);

    useEffect(() => {
      if (localRsnCallbackNeeded === true && cachedLocalRsnData !== null) {
        console.debug(
          "[ZipcodeModule] sent local/rsn's to subscriber (lagged callback)"
        );
        try {
          onLocalRsnChnlsConfirmed(
            cachedLocalRsnData,
            cachedDmas?.dma,
            cachedDmas?.nielsenDma
          );
          triggerSuccessNotification();
        } catch {
          return;
        }

        const isLocalsEligible = Boolean(
          cachedLocalRsnData?.localChannels?.length > 0
        )?.toString();
        window.sessionStorage.setItem("isLocalsEligible", isLocalsEligible);

        setLocalRsnCallbackNeeded(false);
      }
    }, [localRsnCallbackNeeded, cachedLocalRsnData]);

    // To check the zipcode present in model or page
    useEffect(() => {
      if (window.self !== window.top) {
        setViewContext('modal');
      }
    }, []);
    /** --------------------------------------------------------------------- */

    const countySelectionProp = useMemo(
      () => countySelection?.getKey() || "",
      [countySelection]
    );

    const countyOptionProps = useMemo(() => (inputZip?.length === 5 && countyOptions?.length ?
      countyOptions?.map((op: County) => ({
        value: op.getKey(),
        label: op.getLabel()
      })) :
      []), [countyOptions]);

    const zipCodeMessage = useMemo(() => {
      if (zipInputError) {
        return zipCodeInputErrorMsg;
      } else if (countyNoResults) {
        return zipCodeLookupErrorMsg;
      }
      return "";
    }, [
      zipInputError,
      zipCodeInputErrorMsg,
      countyNoResults,
      zipCodeLookupErrorMsg
    ]);

    const countyMessage = useMemo(() => {
      if (countySelectionError) {
        return countySelectionErrorMsg;
      }
      return "";
    }, [countySelectionError, countySelectionErrorMsg]);

    const dynamicNotificationStyle = React.useCallback(
      showMsgCondition => ({
        overflow: "hidden",
        "& .MuiPaper-root .MuiAlert-message": { overflow: "hidden" }, // hide rogue scrollbar
        "& .MuiTypography-root": { fontFamily: "roboto-medium !important" }, // override "Roboto"
        maxHeight:
          !isVisible || !showMsgCondition ? 0 : {
            xs: "120px",
            md: "80px"
          }, // value slightly more than max height on viewport
        "& .MuiPaper-root": {
          alignItems: "center",
          maxHeight:
            !isVisible || !showMsgCondition ? 0 : {
              xs: "120px",
              md: "80px"
            }, // value slightly more than max height on viewport
          margin: "0px 0px !important"
        },
        "& .MuiAlert-standardSuccess, & .MuiAlert-standardError": {
          margin: "0 !important"
        },

        ...(!isVisible ?
          {
            // hide message with drawer collapse
            transition: `max-height 100ms cubic-bezier(0.4, 0, 0.2, 1)`,
            "& .MuiPaper-root": {
              transition: `max-height 100ms cubic-bezier(0.4, 0, 0.2, 1)`,
              "& .MuiAlert-icon svg": {
                maxHeight: 0,
                opacity: 0,
                transition:
                    "max-height 100ms cubic-bezier(0.4, 0, 0.2, 1) 100ms, opacity 100ms cubic-bezier(0.4, 0, 0.2, 1)"
              }
            }
          } :
          !showMsgCondition ?
            {
              // hide message timeout animation
              // perspective sets how "far" the object is from camera in 3D space. Rotating down from -90deg with transformOrigin top gives a "fall down" effect
              transform:
                "perspective(1000px) rotateX(-90deg) translateY(0) translateZ(0)",
              // delay transform/max-height animations so they are "secret"
              transition: `transform 0ms cubic-bezier(0, .5, 1, 1) 230ms, max-height 200ms cubic-bezier(0.4, 0, 0.2, 1) 230ms`,
              "& .MuiPaper-root": {
                position: "relative",
                top: "-110%",
                // slide top/margin up first, then collapse height/padding
                transition: `top 230ms cubic-bezier(0.4, 0, 0.2, 1), margin 180ms cubic-bezier(0.4, 0, 0.2, 1) 50ms,
            max-height 100ms cubic-bezier(0.4, 0, 0.2, 1) 230ms, padding 100ms cubic-bezier(0.4, 0, 0.2, 1) 230ms`,
                "& .MuiAlert-icon svg": {
                  opacity: 0,
                  maxHeight: 0,
                  transition:
                    "max-height 180ms cubic-bezier(0.4, 0, 0.2, 1) 50ms, opacity 180ms cubic-bezier(0.4, 0, 0.2, 1) 50ms"
                }
              }
            } :
            {
              // show message animation
              transform:
                "perspective(1000px) rotateX(0deg) translateY(0) translateZ(0)",
              // timing starts faster
              transition: `transform 180ms cubic-bezier(0, .5, 1, 1), max-height 100ms cubic-bezier(0, 0.5, 1, 1)`,
              "& .MuiPaper-root": {
                position: "relative",
                top: 0
              }
            })
      }),
      [isVisible, countyApiError, countyApiHanging, showSuccessMsg]
    );

    return (
      <DtvBox
        id={id}
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: hAlignment,
          marginBottom: "5px"
        }}
      >
        <DtvBox
          id={`${id}-flex-wrp`}
          sx={{
            width: "100%",
            containerType: "inline-size",
            containerName: "zmRoot",
            display: "flex",
            flexDirection: "column",
            alignItems: hAlignment,
            zIndex: 900,
            ...(position === "center" && {
              maxWidth: "900px",
              [down(bps.values.md)]: {
                maxWidth: "700px"
              }
            })
          }}
        >
          {/** Header Text */}
          <DtvBox
            id={`${id}-header-ctnr`}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              [up(bps.values.sm)]: {
                flexDirection: "row"
              },
              [down(bps.values.sm)]: {
                padding: position === "center" ? "0px 12px" : "0px"
              },
              alignItems: "center",
              justifyContent: hAlignment
            }}
          >
            {/* Grouped clickable text */}
            <DtvBox
              id="zm-dynamic-block"
              role="button"
              tabIndex={0}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: hAlignment,
                cursor: "pointer"
              }}
              onBlur={() => setChangeFocused(false)}
              onClick={toggleVisibility}
              onFocus={() => setChangeFocused(true)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleVisibility();
                }
              }}
            >
              {/* Text 1 */}

              <DtvText
                component="div"
                content={displayText || "Showing channels for "}
                variant={displayTextVariant}
                sx={{
                  color: mainTextColor,
                  [down(bps.values.sm)]: {
                    fontSize: position === "left" ? "16px" : "20px"
                  },
                  [up(bps.values.md)]: {
                    fontSize: position === "left" ? "20px" : "24px"
                  }
                }}
              />
              {/** Text2  */}
              <DtvText
                component="div"
                content={`&nbsp${  zipCodeLabel || "ZIP Code "}`}
                variant={displayTextVariant}
                sx={{
                  color: changeTextColor,
                  [down(bps.values.sm)]: {
                    fontSize: position === "left" ? "16px" : "20px"
                  },
                  [up(bps.values.md)]: {
                    fontSize: position === "left" ? "20px" : "24px"
                  },
                  display: "inline-flex"
                }}
              />

              {/* ZIP Code */}
              {displayZip ? (
                <DtvText
                  component="div"
                  content={`&nbsp${  displayZip  },&nbsp`}
                  variant={displayTextVariant}
                  sx={{
                    color: changeTextColor,
                    cursor: "pointer",
                    [down(bps.values.sm)]: {
                      fontSize: position === "left" ? "16px" : "20px"
                    },
                    [up(bps.values.md)]: {
                      fontSize: position === "left" ? "20px" : "24px"
                    },
                    display: "inline-flex"
                  }}
                />
              ) : (
                <>
                  <LoadingDots mode={mode} />
                  <DtvText content={"&nbsp,"} />
                </>
              )}

              {/* County + Toggle Icon wrapper: keeps county name and arrow together on wrap (position left, mobile) */}
              <DtvBox
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  ...(position === "left" && { flexWrap: "nowrap" })
                }}
              >
                {/* County */}
                {displayCounty ? (
                  <DtvText
                    component="span"
                    content={displayCounty?.getLabel()}
                    variant={displayTextVariant}
                    sx={{
                      color: changeTextColor,
                      [down(bps.values.sm)]: {
                        fontSize: position === "left" ? "16px" : "20px"
                      },
                      [up(bps.values.md)]: {
                        fontSize: position === "left" ? "20px" : "24px"
                      },
                      display: "inline-flex"
                    }}
                  />
                ) : (
                  <LoadingDots mode={mode} />
                )}
                {displayTooltip?.content && (
                  <DtvText
                    content=""
                    sx={{ pl: "4px" }}
                    tooltip={{
                      ...displayTooltip,
                      sx: { color: mainTextColor }
                    }}
                  />
                )}

                {/* Toggle Icon */}
                <DtvBox
                  aria-label={!isVisible ? drawerOpenText : drawerCloseText}
                  id={"hide-chng-ctnr"}
                  role="button"
                  tabIndex={0}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    transform: !isVisible ? "rotate(0deg)" : "rotate(180deg)",
                    transition: "transform 200ms ease-in-out",
                    padding: "6px",
                    "& span": {
                      fontSize: "16px !important",
                      fontFamily: "roboto-medium !important"
                    },
                    lineHeight: "24px",
                    color: `${changeTextColor}`,
                    border: "none !important",
                    fontFamily: "roboto-medium !important",
                    "&:visited": {
                      color: `${changeTextColor} !important`
                    },
                    "& .MuiLink-root:focus": {
                      textDecoration: "underline"
                    },
                    "& .changeHideIcon": {
                      height: {
 xs: 16,
sm: 20,
md: 24
}
                    }
                  }}
                >
                  <svg
                    className="changeHideIcon"
                    fill="none"
                    viewBox="0 0 14 17"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.23092 6.48906C2.23092 6.21233 2.33175 5.93105 2.5378 5.71783C2.94989 5.29592 3.61626 5.29592 4.02396 5.71783L7.77662 9.6103L11.538 5.70875C11.9501 5.2823 12.6121 5.2823 13.0242 5.70875C13.4363 6.1352 13.4363 6.82024 13.0242 7.24669L8.81561 11.611C8.5438 11.8923 8.16678 12.0556 7.77223 12.0556C7.38206 12.0556 7.00065 11.8968 6.72885 11.6155L2.52905 7.2603C2.323 7.04708 2.22217 6.77034 2.22217 6.4936"
                      fill={changeTextColor}
                    />
                  </svg>
                </DtvBox>
              </DtvBox>
            </DtvBox>
          </DtvBox>

          {/** Toggled Content */}
          <DtvBox
            id={`${id}-toggled-content`}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              justifyContent: hAlignment,
              width: "100%",
              flexWrap: "nowrap",
              [position === "left" ?
                down(bps.values.sm) :
                bps.down(bps.values.md)]: {
                flexWrap: "wrap"
              },
              ...(isVisible ?
                {
                    overflow: "visible",
                    maxHeight: "230px",
                    paddingTop: "10px",
                    [up(bps.values.sm)]: {
                      maxHeight: "250px",
                      paddingTop: "12px"
                    },
                    opacity: 1,
                    // opening
                    transition: `max-height 226ms cubic-bezier(0.4, 0, 0.2, 1), padding 96ms ease-out 50ms, opacity 256ms cubic-bezier(0.4, 0, 0.2, 1)`
                  } :
                {
                    overflow: "hidden",
                    maxHeight: "0px",
                    paddingTop: "0px",
                    opacity: 0,
                    // closing
                    transition: `max-height 226ms cubic-bezier(0.6, 0, 0.4, 1) 50ms, padding 96ms ease-in 100ms, opacity 256ms cubic-bezier(0.2, 0, 0.4, 1) 50ms`
                  })
            }}
          >
            {/** Input Fields */}
            <DtvBox
              id={`${id}-inputs-container`}
              sx={{
                width: "100%",
                [up(bps.values.sm)]: {
                  display: "flex",
                  flexDirection: "row"
                },
                [down(bps.values.sm)]: {
                  display: "flex",
                  flexDirection: "column"
                }
              }}
            >
              <DtvBox
                id={`${id}-zip-ctnr`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexBasis: "100%",
                  padding: 0,
                  [up(bps.values.sm)]: {
                    padding: "0px 12px 0px 0px !important",
                    flexBasis: "200px"
                  },
                  transition: "padding 100ms ease",
                  fontFamily: "roboto-regular !imoprtant",
                  // Base <div> tag
                  "& .MuiFormControl-root, & .DtvFormControl-root": {
                    height: "48px",
                    [bps.up(bps.values.md)]: {
                      height: size === "small" ? "48px" : "56px !important"
                    },
                    transition: `height 196ms ${
                      isVisible ?
                        "cubic-bezier(0.4, 0, 0.2, 1)" :
                        "cubic-bezier(0.2, 0, 0.4, 1)"
                    }`
                  },
                  "& .MuiInputBase-root": {
                    height: "48px",
                    [bps.up(bps.values.md)]: {
                      height: size === "small" ? "48px" : "56px !important"
                    }
                  },
                  "& .MuiInputLabel-root, .MuiInputLabel-shrink, .MuiFormLabel-root":
                    {
                      fontFamily: "roboto-medium !important"
                    }
                }}
              >
                <TextField
                  helperMessageIcon
                  showClearIcon
                  aria-label={zipCodeLabel ? zipCodeLabel : "enter zip code"}
                  error={Boolean(zipCodeMessage)}
                  helperMessageText={zipCodeMessage}
                  iconVariant="clearIcon"
                  label={zipCodeLabel}
                  mode={mode}
                  role="textbox"
                  showHelperMessage={Boolean(zipCodeMessage)}
                  size={size}
                  tabIndex={isVisible ? 0 : -1}
                  type="zipcode"
                  value={inputZip}
                  width="100%"
                  inputProps={{
                    role: "textbox",
                    "aria-label": inputZip
                  }}
                  onChange={handleZipInputChange}
                  onClear={onZipcodeCleared}
                />
              </DtvBox>
              <DtvBox
                id={`${id}-county-ctnr`}
                sx={{
                  display: "flex",
                  width: "100%",
                  backgroundColor: "transparent",
                  flexBasis: "100%",
                  padding: "12px 0 0 0",
                  [up(bps.values.sm)]: {
                    padding: "0 12px 0 0",
                    flexBasis: "calc(100% - 200px)"
                  },
                  transition: `flex-basis 200ms ease`,
                  fontFamily: "roboto-regular !important",
                  // Use roboto-regular for label/placeholder only
                  "& .MuiInputLabel-root, & .MuiInputLabel-shrink, & .MuiFormLabel-root":
                    {
                      fontFamily: "roboto-medium !important"
                    }
                }}
              >
                <TextFieldDropdown
                  helperMessageIcon
                  error={Boolean(countyMessage)}
                  helperMessageText={countyMessage}
                  id="county-dropdown"
                  isLoadingOptions={isCountyApiLoading}
                  isVisible={isVisible}
                  label={countyLabel}
                  maxVisible={3}
                  mode={mode}
                  options={countyOptionProps}
                  showHelperMessage={Boolean(countyMessage)}
                  size={size}
                  value={countySelectionProp}
                  width="100%"
                  onSelection={handleCountySelectionChange}
                />
              </DtvBox>
            </DtvBox>
            {/** "Update" Button */}
            <Button
              aria-label={`${ctaLabel?.toLocaleLowerCase?.()}`}
              buttontype="primary"
              data-linkdestination={locationHrefurl}
              data-linkposition={"body"}
              mode={mode}
              size={size}
              tabIndex={isVisible ? 0 : -1}
              {...(isCountyApiLoading && { disabled: true })}
              sx={{
                minWidth: "unset !important",
                width: "auto",
                margin: "0",
                fontFamily: "roboto-medium !important",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "130%",
                ...(position === "left" && {
 marginBottom: {
 xs: "4px",
sm: "12px"
}
}),
                // Mode-based styles
                backgroundColor: mode === "light" ? "#F4F4F4" : "#1F3A56",
                color: mode === "light" ? "#2E70BD" : "#FFFFFF",
                height: size === "small" ? "48px" : "56px",
                // height: "56px",
                // padding: 4px 32px
                padding: size === "small" ? "4px 20px" : "4px 32px",
                fontSize: "16px",
                ...(position === "left" ?
                  {
                      [down(bps.values.md)]: {
                        height: "48px",
                        padding: "4px 20px",
                        fontSize: "16px"
                      },
                      [down(bps.values.sm)]: {
                        height: "36px",
                        padding: "4px 20px",
                        fontSize: "14px"
                      }
                    } :
                  {
                      [down(bps.values.md)]: {
                        height: "48px",
                        padding: "4px 20px",
                        fontSize: "16px"
                      },
                      [down(bps.values.sm)]: {
                        height: "36px",
                        padding: "4px 20px",
                        fontSize: "14px"
                      }
                    }),

                [up(bps.values.md)]: {
                  transition: `height 196ms ${
                    isVisible ?
                      "cubic-bezier(0.4, 0, 0.2, 1)" :
                      "cubic-bezier(0.2, 0, 0.4, 1)"
                  }`
                },

                "& span": {
                  fontFamily: "roboto-medium"
                },
                "&:focus-within": {
                  border: "transparent",
                  borderColor: "#1d4678",
                  color: "#ffffff",
                  backgroundColor: "#1d4678"
                }
              }}
              onClick={handleSearchButtonClick}
            >
              {ctaLabel}
            </Button>
          </DtvBox>

          {/** Notifications */}
          <DtvBox
            aria-label="Update Successful"
            sx={{
              width: "100%",
              margin: "8px 0 0 0",
              ...(position === "center" && {
                maxWidth: "900px",
                [down(bps.values.sm)]: { maxWidth: "700px" }
              }),
              ...dynamicNotificationStyle(showSuccessMsg)
            }}
          >
            <DtvAlert
              oneRow
              ariaLabel="Update Successful"
              severity="success"
              title="Update Successful"
            />
          </DtvBox>
          <DtvBox
            aria-label="Error"
            sx={{
              width: "100%",
              margin: "8px 0 0 0",
              ...(position === "center" && {
                maxWidth: "900px",
                [down(bps.values.sm)]: { maxWidth: "700px" }
              }),
              ...dynamicNotificationStyle(
                (countyApiError || countyApiHanging) && !showSuccessMsg
              )
            }}
          >
            <DtvAlert
              oneRow
              severity="error"
              ariaLabel={
                countyApiError ?
                  countyApiErrorMsg :
                  countyApiHanging ?
                  countyApiHangingMsg :
                  ""
              }
              title={
                countyApiError ?
                  countyApiErrorMsg :
                  countyApiHanging ?
                  countyApiHangingMsg :
                  ""
              }
            />
          </DtvBox>
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    console.error("ZipCode Module >> Component Error:", error);
    return <></>;
  }
});
